import type { BankBreakMode, SimEvent } from '../shared/contract';
import {
  ANTIC, SNAP, SETTLE, BEAT, DRIFT, FLICKER, RATCHET, DOLLY, SLOWMO,
} from '../render-kit/src/anim/tween';

/**
 * SPIELHÖLLE — the whole audio identity, in one file.
 *
 * **There are no audio files in this game.** Every sound below is built at runtime out of
 * oscillators, biquads and noise buffers this module generates itself. That is a decision,
 * not a shortcut: no download, no provenance to defend in LICENSES.md, no bundle weight,
 * and a coin ping whose pitch can be driven by the coin's own value. See
 * `render-kit/LICENSES.md` § "Audio".
 *
 * ## The doctrine this file obeys
 *
 * ART-BIBLE §0: *"1987 Autobahn-Raststätte arcade after midnight, drawn as anime."*
 * Cozy-sinister deadpan. **Never horror, never Vegas.** So: the jackpot is a cheap struck
 * bell and a three-note chiptune arpeggio played completely straight, not a casino siren.
 * The boss telegraph is a station-PA descending fourth, not a tritone. The room tone is a
 * fan and a mains hum, not a drone.
 *
 * ART-BIBLE §7.1: the named timing constants are the **only** timings allowed, and they are
 * imported from the kit rather than copied, so a tuning pass moves picture and sound
 * together. RATCHET is one reel detent *and* one printed receipt line, which is why the
 * till and the reels are audibly the same mechanism. FLICKER quantises the neon's wobble.
 *
 * ART-BIBLE §7.2 law 3 — **the House moves linearly, the player's luck eases** — has a
 * direct audio translation, and it is the single most useful rule in here:
 *
 *   - House sounds (`glide: 'house'`) sweep frequency with `exponentialRampToValueAtTime`,
 *     which is a *constant number of semitones per second* — a dead straight glide in the
 *     domain the ear actually hears — and fade amplitude with a straight `linearRamp`, so
 *     they stop mechanically rather than dying away. Reel detents, the till, the pusher
 *     rumble, Der Rausschmeißer's boots, the ledger, the boss telegraph.
 *   - Luck sounds (`glide: 'luck'`) overshoot their target pitch by ~8% and settle back
 *     over SETTLE, and decay exponentially. GLÜCK, STRÄHNE, the coin rain, the jackpot.
 *
 * ART-BIBLE §7.3: only one big spectacle at a time. `bankBreak`, `jackpot`, `abrechnung`
 * and `receipt` take the `spectacle` bus and duck the other three buses for their duration.
 * The kit *queues* its big events; this module does not (a bankBreak heard two seconds
 * after it is seen is worse than no bankBreak), it **steals** the slot when the incoming
 * spectacle outranks the running one and **demotes** it onto the machine bus at reduced
 * gain otherwise. That divergence is deliberate and is the one place picture and sound
 * disagree on purpose.
 *
 * ## The mix rules (the actual difficulty)
 *
 * Ten minutes of play must not grate, and the sound that fires most is also the signature
 * one. Three mechanisms, all in `mixPolicy`, all pure and all unit-tested:
 *
 *   1. **Voice cap** — at most `VOICE_CAP` scheduled cues alive at once. Over cap, the
 *      weakest voice (lowest priority, then quietest, then oldest) is cut in 8 ms if it is
 *      weaker than the newcomer; otherwise the newcomer is refused. Both count as `stolen`.
 *   2. **Per-sound-id repeat ducker** — each id carries "heat". Firing adds 1; heat drains
 *      one unit per `REPEAT_RECOVER` seconds. Gain is `FLOOR + (1-FLOOR)·STEP^heat` and the
 *      pitch bends *down* with heat, so a machine-gunned sound retreats and dulls instead of
 *      turning into a buzzsaw. It never reaches zero — a silent reel detent reads as a bug.
 *   3. **Pitch variation** — every repeatable sound is detuned by a deterministic jitter
 *      derived from the event's own fields (token id, machine, value). Same run, same seed,
 *      same sound. House sounds get `HOUSE_JITTER_CENTS` (±4) rather than ±34: enough to
 *      break comb-filtering between two boots landing, not enough to read as expressive.
 *      Der Rausschmeißer does not emote.
 *
 * Plus a `DynamicsCompressorNode` limiter across the master, and four buses
 * (`ambient` / `machine` / `spectacle` / `ui`) so a big event can duck the bed.
 *
 * ## Measured, not claimed
 *
 * There is no way to hear this box, so the mix was checked the only honest way available:
 * rendering the module's own graph through an `OfflineAudioContext` in headless Chromium
 * and measuring peak and RMS. Current numbers, at `volume: 1`, per sound in isolation:
 *
 * ```
 *   room + neon + pusher bed          RMS 0.018 (−35 dBFS), peak 0.064
 *   bed under a running spectacle     RMS 0.005 (−46 dBFS)   ← the duck, working
 *   texture (coin tick, Zins, PA)     peak 0.13 – 0.14
 *   machine mechanics (detent, TOPF)  peak 0.18 – 0.26
 *   coin rain                         peak 0.39 – 0.42
 *   bank breaks (glass…tray)          peak 0.45 – 0.84
 *   60 scores + bankBreak + jackpot   peak 0.55, 26 played / 38 stolen / 59 ducked
 * ```
 *
 * Two things that pass every API test and are still wrong were found that way and are
 * documented at the code that fixes them: the master compressor was flattening every coin
 * in the game (see `build()`), and the beds all started at phase zero on the same sample
 * and thumped (see `buildBeds()`).
 *
 * ## Determinism and safety
 *
 * This module is a **pure sink**. It never calls into the sim, never uses `Math.random`
 * (there is one 32-bit hash and a counter-based stream, both seeded from event fields), and
 * never blocks. Nothing it does can change a snapshot.
 *
 * It must also survive a headless Playwright page with no `AudioContext` and no user
 * gesture. So: the context is created inside `unlock()` and nowhere else, every public
 * method swallows its own errors, and with no context the whole thing degrades to bare
 * bookkeeping — the mix policy still runs, `stats()` still counts, nothing is scheduled and
 * **nothing is ever written to the console**. A console error from this file fails the E2E
 * gate, which is why there is not a single `console.` call below.
 */

// ---------------------------------------------------------------- the Web Audio surface

/**
 * The slice of the Web Audio API this file touches, declared rather than taken from
 * `lib.dom`. Two practical reasons: `test/` compiles under the root tsconfig, which has no
 * DOM lib on purpose (core/ must not be able to see `document`), and this module has to run
 * — silently — inside a plain node process. Declaring the surface makes one file compile
 * under both tsconfigs and documents exactly how much API we actually depend on: seven node
 * types, no worklets, no panners, no analysers.
 */
interface WParam {
  value: number;
  setValueAtTime(v: number, t: number): WParam;
  linearRampToValueAtTime(v: number, t: number): WParam;
  exponentialRampToValueAtTime(v: number, t: number): WParam;
  setTargetAtTime(v: number, t: number, timeConstant: number): WParam;
  cancelScheduledValues(t: number): WParam;
}
interface WNode {
  connect(dest: WNode | WParam): unknown;
  disconnect(): void;
}
interface WGain extends WNode { gain: WParam }
interface WOsc extends WNode {
  type: string;
  frequency: WParam;
  detune: WParam;
  start(t?: number): void;
  stop(t?: number): void;
  onended: (() => void) | null;
}
interface WFilter extends WNode { type: string; frequency: WParam; Q: WParam; gain: WParam }
interface WBuffer { length: number; duration: number; sampleRate: number; getChannelData(ch: number): Float32Array }
interface WBufferSource extends WNode {
  buffer: WBuffer | null;
  loop: boolean;
  loopStart: number;
  loopEnd: number;
  playbackRate: WParam;
  start(t?: number, offset?: number): void;
  stop(t?: number): void;
  onended: (() => void) | null;
}
interface WComp extends WNode {
  threshold: WParam; knee: WParam; ratio: WParam; attack: WParam; release: WParam;
}
interface WCtx {
  currentTime: number;
  sampleRate: number;
  state: string;
  destination: WNode;
  createGain(): WGain;
  createOscillator(): WOsc;
  createBiquadFilter(): WFilter;
  createBufferSource(): WBufferSource;
  createBuffer(channels: number, length: number, rate: number): WBuffer;
  createDynamicsCompressor(): WComp;
  resume(): Promise<void>;
  close(): Promise<void>;
}
type WCtxCtor = new (opts?: { latencyHint?: string }) => WCtx;

/** Feature detection, done once and never assumed. `null` means "be a silent sink". */
function audioCtor(): WCtxCtor | null {
  const g = globalThis as unknown as { AudioContext?: unknown; webkitAudioContext?: unknown };
  const c = g.AudioContext ?? g.webkitAudioContext;
  return typeof c === 'function' ? (c as WCtxCtor) : null;
}

type WaveName = 'sine' | 'square' | 'sawtooth' | 'triangle';
type Stoppable = { stop(t?: number): void };

// ---------------------------------------------------------------- deterministic noise

/**
 * Murmur3's 32-bit finaliser. Stateless, seeded per call from event fields, and the only
 * source of "randomness" anywhere in this module — `Math.random` appears nowhere, so two
 * replays of the same seed and input log sound identical down to the pitch of every ping.
 */
function hash32(x: number): number {
  let h = x >>> 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** A counter-based stream: no state to carry between frames, no chance of drift. */
function rng(seed: number): () => number {
  let n = seed >>> 0;
  return () => {
    n = (n + 0x9e3779b9) >>> 0;
    return hash32(n) / 4294967296;
  };
}

/** FNV-1a over a short string — machine ids, break modes, boss names. */
function strHash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

// ---------------------------------------------------------------- the cue vocabulary

export type Bus = 'ambient' | 'machine' | 'spectacle' | 'ui';

/**
 * What one event turns into. A cue is data — no nodes, no context — which is what makes
 * `soundFor()` testable in node and what lets the mix policy reason about a sound before
 * deciding whether it gets to exist.
 */
export interface Cue {
  /** stable sound id; the key the repeat ducker counts on */
  id: string;
  bus: Bus;
  /** nominal amplitude 0..1 before ducking */
  gain: number;
  /** seconds this cue occupies a voice slot; drives the cap and the spectacle duck */
  dur: number;
  /** 0..9, higher survives the cap. See the scale in `soundFor`. */
  priority: number;
  /** deterministic jitter seed, taken from the event's own fields */
  seed: number;
  /** House sounds get the tiny jitter and the straight ramps */
  house: boolean;
  /** variant discriminator: bankBreak mode, boss/rausschmeißer phase, tags */
  detail?: string;
  /** payout mass for the coin rain */
  mass?: number;
  /** a pre-computed pitch when the event carries one (score value, zins value, impulse) */
  freq?: number;
  /** repeated elements: receipt lines, footsteps, echo taps */
  reps?: number;
}

/** Every id `soundFor` can return. The test asserts the mapping never invents one. */
export const SOUND_IDS = [
  'coin.launch', 'coin.tick', 'coin.rain',
  'score.pling', 'stage.step',
  'topf.slurp', 'jackpot.fanfare', 'bank.break',
  'bust.thud', 'cash.out', 'stakes.up',
  'reel.detent', 'wheel.settle',
  'straehne.ignite', 'straehne.snuff',
  'tilt.buzz', 'tilt.lockout',
  'glueck.spend', 'glueck.regen',
  'tag.split', 'tag.echo', 'tag.zins', 'tag.phase', 'tag.stick', 'pile.settle',
  'verdacht.sting', 'zinken.install', 'zinken.reveal', 'zinken.pry',
  'abrechnung.ledger', 'till.print',
  'floor.start', 'floor.end', 'quota.met',
  'boss.door', 'boss.telegraph', 'boss.end',
  'rausschmeisser.step', 'rausschmeisser.shove', 'rausschmeisser.win',
  'hausverbot.stamp', 'claw.grab', 'claw.miss', 'item.get', 'item.lose',
] as const;

export type SoundId = (typeof SOUND_IDS)[number];

// ---------------------------------------------------------------- the mix policy

/**
 * The three mix rules, as numbers and pure functions. Everything here is deliberately
 * free of audio nodes so the policy can be argued with in a unit test rather than by ear
 * at two in the morning.
 */
export const mixPolicy = {
  /**
   * 24 simultaneous *cues* — not oscillators. A coin rain is one cue that schedules up to
   * RAIN_MAX_PINGS pings, because the signature sound of the game must not be able to lose
   * a fight with twelve collision ticks. 24 was picked by counting the worst honest frame
   * in a payout storm (a bankBreak plus a STRÄHNE plus a pile of sediment ≈ 18) and
   * leaving headroom; above ~30 the limiter is doing all the work and nothing is legible.
   */
  VOICE_CAP: 24,

  /** heat drains one unit per this many seconds. Just under BEAT: two deliberate presses
   *  a beat apart are both at full volume; a burst inside one beat is not. */
  REPEAT_RECOVER: 0.45,
  /** each unit of heat multiplies what is left above the floor by this */
  REPEAT_STEP: 0.62,
  /** a repeated sound never disappears — below about a quarter it reads as a dropout */
  REPEAT_FLOOR: 0.25,
  /** cents *down* per unit of heat: a hammered mechanism sags, it does not rise */
  REPEAT_DETUNE: 19,
  REPEAT_DETUNE_MAX: 70,

  /** ±cents of deterministic pitch jitter on ordinary sounds */
  JITTER_CENTS: 34,
  /** ±cents for House machinery. Enough to stop two boots comb-filtering, not enough to emote. */
  HOUSE_JITTER_CENTS: 4,

  /** contract collisions are already throttled on the way out; this is the second gate */
  COLLISION_MIN_IMPULSE: 0.9,
  /** hard cap per pushed frame, before the ducker even sees them */
  COLLISION_PER_FRAME: 3,

  /** what a running spectacle does to the other three buses */
  SPECTACLE_DUCK: { ambient: 0.30, machine: 0.26, ui: 0.55 } as Record<Bus, number>,
  /** a spectacle that loses the slot still plays, this much quieter, on the machine bus */
  SPECTACLE_DEMOTE_GAIN: 0.55,

  /** the coin rain's oscillator budget. 48 pings ≈ 300 nodes for ~1.2 s; measured fine. */
  RAIN_MAX_PINGS: 48,
  /** a 200-line receipt would otherwise schedule 200 stepper clicks */
  RECEIPT_MAX_STEPS: 48,

  /** heat left after `dt` seconds of silence */
  decayHeat(heat: number, dt: number): number {
    return Math.max(0, heat - Math.max(0, dt) / mixPolicy.REPEAT_RECOVER);
  },

  /** gain multiplier for a sound fired at this much accumulated heat. 1 at heat 0. */
  repeatGain(heat: number): number {
    const f = mixPolicy.REPEAT_FLOOR;
    return f + (1 - f) * Math.pow(mixPolicy.REPEAT_STEP, Math.max(0, heat));
  },

  /** cents of downward detune for a sound fired at this much heat (always ≤ 0) */
  repeatDetune(heat: number): number {
    if (heat <= 0) return 0;   // not `-0`: it is the same pitch, but it is not the same number
    return -Math.min(mixPolicy.REPEAT_DETUNE_MAX, mixPolicy.REPEAT_DETUNE * heat);
  },

  /** deterministic ±cents from a seed */
  jitter(seed: number, cents: number): number {
    return (hash32(seed) / 4294967296 - 0.5) * 2 * cents;
  },

  /**
   * Which voice loses its slot. Returns an index to steal, or -1 meaning "refuse the
   * newcomer". Weakest = lowest priority, then quietest, then oldest; since the newcomer's
   * `startedAt` is always the newest, an all-equal field steals the oldest voice, which is
   * the behaviour you want for coin ticks (the newest tick is the one you can see).
   */
  victim(voices: readonly VoiceRank[], incoming: VoiceRank): number {
    if (voices.length === 0) return -1;
    let best = 0;
    for (let i = 1; i < voices.length; i++) if (weaker(voices[i], voices[best])) best = i;
    return weaker(voices[best], incoming) ? best : -1;
  },

  /**
   * ART-BIBLE §7.3, as a decision function. A running spectacle keeps the room unless the
   * newcomer outranks it; the loser is demoted rather than queued (see the header).
   */
  spectacleAction(
    running: { priority: number; until: number } | null,
    incoming: { priority: number },
    now: number,
  ): 'take' | 'demote' {
    if (!running || now >= running.until) return 'take';
    return incoming.priority > running.priority ? 'take' : 'demote';
  },
};

export interface VoiceRank { priority: number; amp: number; startedAt: number }

function weaker(a: VoiceRank, b: VoiceRank): boolean {
  if (a.priority !== b.priority) return a.priority < b.priority;
  if (a.amp !== b.amp) return a.amp < b.amp;
  return a.startedAt < b.startedAt;
}

/**
 * How a payout's mass becomes a coin shower. Square root, not linear: a 4000-coin burst is
 * perhaps four times as *loud* as a 250-coin one, not sixteen times, and it must never try
 * to schedule 4000 oscillators. The window grows more slowly than the count on purpose —
 * a big payout should get **denser**, not merely longer, because density is what reads as
 * mass. A long thin trickle sounds like a small win no matter how many coins are in it.
 */
export function coinRainShape(mass: number): { count: number; window: number; ping: number } {
  const m = Math.max(0, mass);
  const count = Math.min(mixPolicy.RAIN_MAX_PINGS, Math.max(3, Math.round(3 + Math.sqrt(m) * 2.1)));
  const window = Math.min(2.4 * BEAT, SNAP + Math.sqrt(m) * 0.055);
  // energy per ping falls as the shower thickens, so the sum stays inside the limiter
  const ping = 1 / Math.sqrt(count);
  return { count, window, ping };
}

// ---------------------------------------------------------------- event → sound

/**
 * The whole mapping, in one exhaustive switch. Priority scale:
 *
 *   9  bankBreak — the money shot, outranks everything including a running jackpot
 *   8  jackpot, abrechnung, receipt, bossEnd — the other spectacles
 *   7  payout, cashOut — the coin rain; the player's whole reason for being here
 *   6  run-structure beats: bust, lockout, STRÄHNE, floors, quota, boss start, the shove
 *   5  machine mechanics you act on: detents, wheel, TOPF, TILT, GLÜCK, claw, items
 *   4  per-token results: score, split, echo, Zins, drop, payout stages
 *   2  texture: collisions, [Kleber], [Geist], sediment
 *
 * Returns `null` only where silence is correct: a collision under the impulse threshold
 * (it is a pile rustling, and the pile is the bed's job) and a `sediment` event that is
 * merely un-packing a token, which is bookkeeping and not an audible act.
 */
export function soundFor(ev: SimEvent, machineSeed = 0): Cue | null {
  const s = machineSeed >>> 0;
  switch (ev.t) {
    case 'drop':
      return { id: 'coin.launch', bus: 'machine', gain: 0.55, dur: 0.22, priority: 4, seed: s ^ hash32(ev.token), house: false };

    case 'collision': {
      if (ev.impulse < mixPolicy.COLLISION_MIN_IMPULSE) return null;
      // impulse → both loudness and pitch: a hard hit is brighter as well as louder, which
      // is the only cue the ear has for "that was the peg, not the pile".
      const hit = clamp(ev.impulse / 8, 0.05, 1);
      return {
        id: 'coin.tick', bus: 'machine', gain: 0.10 + hit * 0.26, dur: 0.09, priority: 2,
        seed: s ^ hash32(ev.a * 7919 + (ev.b ?? 0)), house: false, freq: 1400 + hit * 2200,
      };
    }

    case 'score': {
      // pitch from value, log-scaled: doubling the score is one octave, so the pocket you
      // want to hit is the one that sounds high. tags ride along as timbre (see render()).
      const v = Math.max(1, ev.value);
      return {
        id: 'score.pling', bus: 'machine', gain: 0.44, dur: 0.42, priority: 4,
        seed: s ^ hash32(ev.token * 31 + v), house: false,
        freq: 520 * Math.pow(2, clamp(Math.log2(v) * 0.28, 0, 2.2)),
        detail: ev.tags.join('+'),
      };
    }

    case 'payoutStage':
      // the game shows its math; the ledger steps are House machinery, on the grid.
      return { id: 'stage.step', bus: 'machine', gain: 0.20, dur: RATCHET * 2, priority: 4, seed: s ^ strHash(ev.stage), house: true };

    case 'payout': {
      const mass = ev.muenzen + ev.tickets;
      return {
        id: 'coin.rain', bus: 'machine', gain: 0.85, dur: coinRainShape(mass).window + 0.25,
        priority: 7, seed: s ^ hash32(mass * 2654435761), house: false, mass,
      };
    }

    case 'topfFeed':
      return { id: 'topf.slurp', bus: 'machine', gain: 0.5, dur: SETTLE + 0.1, priority: 5, seed: s ^ hash32(ev.amount), house: false, mass: ev.amount };

    case 'jackpot':
      return { id: 'jackpot.fanfare', bus: 'spectacle', gain: 0.8, dur: SLOWMO + coinRainShape(ev.value).window, priority: 8, seed: s ^ hash32(ev.value), house: false, mass: ev.value };

    case 'bankBreak':
      return {
        id: 'bank.break', bus: 'spectacle', gain: 0.95,
        dur: ANTIC + SLOWMO + coinRainShape(ev.payout).window + 0.4,
        priority: 9, seed: s ^ hash32(ev.payout) ^ strHash(ev.mode), house: false,
        detail: ev.mode, mass: ev.payout,
      };

    case 'bust':
      return { id: 'bust.thud', bus: 'machine', gain: 0.6, dur: SETTLE + BEAT, priority: 6, seed: s ^ 0x51, house: true };

    case 'cashOut':
      return { id: 'cash.out', bus: 'machine', gain: 0.7, dur: coinRainShape(ev.muenzen + ev.tickets).window + 0.3, priority: 7, seed: s ^ hash32(ev.muenzen + 1), house: false, mass: ev.muenzen + ev.tickets };

    case 'nachlegen':
      // the stakes ratchet up one notch. House: a lever, not a flourish.
      return { id: 'stakes.up', bus: 'machine', gain: 0.5, dur: SNAP + SETTLE, priority: 5, seed: s ^ hash32(ev.newStakes), house: true };

    case 'reelStop':
      return { id: 'reel.detent', bus: 'machine', gain: 0.62, dur: RATCHET * 4, priority: 5, seed: s ^ hash32(ev.reel * 101 + ev.symbolToken), house: true, reps: ev.reel };

    case 'wheelSettle':
      return { id: 'wheel.settle', bus: 'machine', gain: 0.6, dur: SETTLE + SNAP, priority: 5, seed: s ^ hash32(ev.pocket * 37 + ev.token), house: true, reps: ev.pocket };

    case 'straehne':
      return ev.to >= ev.from
        ? { id: 'straehne.ignite', bus: 'machine', gain: 0.6, dur: SETTLE + 0.2, priority: 6, seed: s ^ hash32(ev.to), house: false, reps: ev.to }
        : { id: 'straehne.snuff', bus: 'machine', gain: 0.5, dur: SETTLE, priority: 6, seed: s ^ hash32(ev.from), house: false, reps: ev.from };

    case 'tilt':
      return { id: 'tilt.buzz', bus: 'machine', gain: 0.45 + clamp(ev.amount, 0, 1) * 0.3, dur: BEAT, priority: 5, seed: s ^ hash32(Math.round(ev.amount * 1000)), house: true };

    case 'tiltLockout':
      return { id: 'tilt.lockout', bus: 'machine', gain: 0.7, dur: SETTLE + BEAT, priority: 6, seed: s ^ hash32(ev.until), house: true };

    case 'glueckSpent':
      return { id: 'glueck.spend', bus: 'ui', gain: 0.6, dur: SETTLE + SNAP, priority: 5, seed: s ^ strHash(ev.stream + ev.event), house: false, detail: ev.stream };

    case 'glueckRegen':
      return { id: 'glueck.regen', bus: 'ui', gain: 0.4, dur: BEAT, priority: 4, seed: s ^ hash32(ev.amount), house: false };

    case 'split':
      return { id: 'tag.split', bus: 'machine', gain: 0.45, dur: SETTLE, priority: 4, seed: s ^ hash32(ev.parent * 13), house: false };

    case 'echo':
      return { id: 'tag.echo', bus: 'machine', gain: 0.4, dur: RATCHET * 8, priority: 4, seed: s ^ hash32(ev.token * 17 + ev.value), house: false, freq: 640 * Math.pow(2, clamp(Math.log2(Math.max(1, ev.value)) * 0.2, 0, 1.6)), reps: 3 };

    case 'zinsTick':
      // you can hear the coin double: one octave-ish step per doubling of its value.
      return { id: 'tag.zins', bus: 'machine', gain: 0.38, dur: RATCHET * 3, priority: 4, seed: s ^ hash32(ev.token * 7 + ev.newValue), house: false, freq: 380 * (1 + Math.log2(Math.max(1, ev.newValue)) * 0.16) };

    case 'phase':
      return { id: 'tag.phase', bus: 'machine', gain: 0.22, dur: SETTLE, priority: 2, seed: s ^ hash32(ev.token * 3), house: false };

    case 'stick':
      return { id: 'tag.stick', bus: 'machine', gain: 0.3, dur: SNAP + 0.06, priority: 2, seed: s ^ hash32(ev.token * 5), house: false };

    case 'sediment':
      // un-packing is bookkeeping. Packing is a coin joining the terrain: a small dull thud.
      return ev.packed
        ? { id: 'pile.settle', bus: 'machine', gain: 0.16, dur: SNAP, priority: 2, seed: s ^ hash32(ev.token * 11), house: false }
        : null;

    case 'verdacht':
      return { id: 'verdacht.sting', bus: 'ui', gain: 0.36 + clamp(Math.abs(ev.delta) / 40, 0, 0.3), dur: SETTLE + 0.2, priority: 5, seed: s ^ strHash(ev.reason), house: true, detail: ev.delta >= 0 ? 'up' : 'down' };

    case 'zinkenInstalled':
      // the House rigging a cabinet: a screwdriver, heard from two aisles away.
      return { id: 'zinken.install', bus: 'ui', gain: 0.28, dur: SETTLE, priority: 5, seed: s ^ strHash(ev.zinken), house: true };

    case 'zinkenDiscovered':
      return { id: 'zinken.reveal', bus: 'ui', gain: 0.5, dur: SETTLE + BEAT, priority: 5, seed: s ^ strHash(ev.zinken), house: true };

    case 'zinkenStolen':
      return { id: 'zinken.pry', bus: 'ui', gain: 0.6, dur: SETTLE + SNAP, priority: 5, seed: s ^ strHash(ev.zinken + '!'), house: false };

    case 'abrechnung': {
      // one key clunk per ledger line: six for the sum, one more per thing he takes off you
      const keys = Math.min(6 + ev.confiscated.length, 12);
      return { id: 'abrechnung.ledger', bus: 'spectacle', gain: 0.7, dur: RATCHET * 2 * keys + BEAT + SETTLE * 2, priority: 8, seed: s ^ hash32(ev.due * 31 + ev.paid), house: true, reps: keys };
    }

    case 'receipt':
      return { id: 'till.print', bus: 'spectacle', gain: 0.72, dur: RATCHET * Math.min(ev.lines.length, mixPolicy.RECEIPT_MAX_STEPS) + BEAT + SETTLE, priority: 8, seed: s ^ hash32(ev.lines.length), house: true, reps: ev.lines.length };

    case 'floorStart': {
      // one struck note per floor number, so how deep you are is something you hear
      const strikes = clamp(ev.floor, 1, 8);
      return { id: 'floor.start', bus: 'ui', gain: 0.55, dur: RATCHET * 3 * strikes + BEAT + SETTLE, priority: 6, seed: s ^ hash32(ev.floor), house: true, reps: strikes };
    }

    case 'floorEnd':
      // Der Croupier wheeling the cabinets downstairs: castors on lino, dead linear.
      return { id: 'floor.end', bus: 'ui', gain: 0.5, dur: DOLLY, priority: 6, seed: s ^ hash32(ev.floor + 900), house: true };

    case 'quotaMet':
      return { id: 'quota.met', bus: 'ui', gain: 0.55, dur: SETTLE + BEAT, priority: 6, seed: s ^ hash32(ev.tickets), house: false };

    case 'bossStart':
      return { id: 'boss.door', bus: 'ui', gain: 0.66, dur: SLOWMO, priority: 6, seed: s ^ strHash(ev.boss), house: true };

    case 'bossPhase':
      return { id: 'boss.telegraph', bus: 'ui', gain: 0.5, dur: BEAT * 2, priority: 5, seed: s ^ strHash(ev.rule) ^ hash32(ev.phase), house: true, reps: ev.phase };

    case 'bossEnd':
      return { id: 'boss.end', bus: 'spectacle', gain: 0.72, dur: SLOWMO + BEAT, priority: 8, seed: s ^ strHash(ev.boss), house: !ev.won, detail: ev.won ? 'win' : 'lose' };

    case 'rausschmeisser':
      switch (ev.phase) {
        // Heavy, slow, LINEAR — he is the House and the House does not emote. Four steps
        // closing at exactly BEAT apart on arrival, three receding when he gives up.
        case 'arrive': return { id: 'rausschmeisser.step', bus: 'ui', gain: 0.62, dur: BEAT * 4 + SETTLE, priority: 6, seed: s ^ 0xb0, house: true, detail: 'arrive', reps: 4 };
        case 'shove': return { id: 'rausschmeisser.shove', bus: 'ui', gain: 0.8, dur: SETTLE + BEAT, priority: 6, seed: s ^ 0xb1, house: true, detail: 'shove' };
        case 'win': return { id: 'rausschmeisser.win', bus: 'ui', gain: 0.7, dur: BEAT * 2, priority: 6, seed: s ^ 0xb2, house: true, detail: 'win' };
        case 'lose': return { id: 'rausschmeisser.step', bus: 'ui', gain: 0.45, dur: BEAT * 3 + SETTLE, priority: 5, seed: s ^ 0xb3, house: true, detail: 'lose', reps: 3 };
      }
      return null;

    case 'hausverbot':
      return { id: 'hausverbot.stamp', bus: 'ui', gain: 0.7, dur: SETTLE + BEAT, priority: 6, seed: s ^ (ev.active ? 0xff : 0x0f), house: true, detail: ev.active ? 'on' : 'off' };

    case 'grab':
      return ev.kept
        ? { id: 'claw.grab', bus: 'machine', gain: 0.6, dur: SETTLE + SNAP, priority: 5, seed: s ^ strHash(ev.prize ?? '-'), house: false }
        : { id: 'claw.miss', bus: 'machine', gain: 0.5, dur: SETTLE, priority: 5, seed: s ^ strHash((ev.prize ?? '-') + '?'), house: true };

    case 'item':
      return ev.gained
        ? { id: 'item.get', bus: 'ui', gain: 0.55, dur: SETTLE + SNAP, priority: 5, seed: s ^ strHash(ev.id), house: false }
        : { id: 'item.lose', bus: 'ui', gain: 0.5, dur: SETTLE, priority: 5, seed: s ^ strHash(ev.id + '-'), house: true };
  }
  // A contract v2 event this module has not learned yet: stay quiet, do not crash.
  return null;
}

// ---------------------------------------------------------------- the module

export interface AudioOpts { enabled?: boolean; volume?: number }

interface Voice extends VoiceRank {
  id: string;
  bus: Bus;
  endsAt: number;
  /** the cue's own gain node, so it can be cut in 8 ms without a click */
  node: WGain | null;
  srcs: Stoppable[];
}

interface Beds {
  fade: WGain;
  roomSrc: WBufferSource; roomLp: WFilter; roomGain: WGain;
  fanOsc: WOsc; fanGain: WGain;
  neonGain: WGain; neonFlicker: WGain; neonOscs: WOsc[]; neonHiss: WBufferSource;
  machGain: WGain; machOsc: WOsc; machNoise: WBufferSource; machBp: WFilter; machLfo: WOsc; machLfoGain: WGain;
  strainOsc: WOsc; strainGain: WGain;
  topfOsc: WOsc; topfGain: WGain;
}

/**
 * Master headroom. `volume` is the player's dial; this is the ceiling it works against, set
 * so the loudest event in the game (a `tray` bank break, measured at 0.84 peak) lands just
 * under full scale with the dial at 1.0 and everything else falls where it falls. Do not
 * "fix" a quiet sound by raising this — raise that sound's own gain in `soundFor`, where it
 * can be compared against its neighbours.
 */
const MASTER_HEADROOM = 0.62;

/**
 * Absolute bed levels, in one table because they only make sense against each other.
 *
 * These are ~7 dB below the first pass. Measured offline, the original bed ran at −24 dBFS
 * RMS with peaks of 0.23 — *louder than a coin tick*, and far louder than anything you want
 * to sit under for ten minutes, which is the brief's actual requirement. They now sit at
 * −35 dBFS with peaks around 0.064: about half the level of the quietest cue in the game
 * (a Zins tick at 0.135), which is where a bed belongs. Present when you listen for it,
 * gone when you are playing.
 */
const BED_LEVEL = { room: 0.063, fan: 0.017, neon: 0.025, mach: 0.042, strain: 0.028, topf: 0.022 };

/** Bed levels per run phase. The room does not go silent — it gets *smaller*. */
const PHASE_BED: Record<string, { room: number; neon: number; mach: number }> = {
  aisle: { room: 1.0, neon: 1.0, mach: 0.22 },
  machine: { room: 0.70, neon: 0.85, mach: 1.0 },
  shop: { room: 0.80, neon: 0.92, mach: 0.35 },
  boss: { room: 0.50, neon: 0.55, mach: 0.18 },
  abrechnung: { room: 0.45, neon: 0.50, mach: 0.10 },
  receipt: { room: 0.45, neon: 0.45, mach: 0.08 },
  over: { room: 0.35, neon: 0.28, mach: 0.0 },
};

/**
 * What each cabinet idles like. All five are always *audible* when you are zoomed in —
 * an arcade machine that makes no noise at all reads as switched off, and "AUSSER BETRIEB"
 * is a state this game uses on purpose.
 */
const MACHINE_BED: Record<string, { osc: number; wave: WaveName; bp: number; q: number; noise: number; lfo: number }> = {
  // the field is quiet; what you hear is the cabinet's own transformer
  pachinko: { osc: 118, wave: 'triangle', bp: 900, q: 0.7, noise: 0.05, lfo: 0.17 },
  // THE pusher rumble: the bar's motor, low and constant. Triangle LFO = linear segments,
  // which is ART-BIBLE §7.2 law 3 encoded in the modulator shape rather than in prose.
  muenzschieber: { osc: 62, wave: 'triangle', bp: 260, q: 0.9, noise: 0.20, lfo: 0.5 },
  // a stepper motor waiting for a lever
  bandit: { osc: 96, wave: 'sawtooth', bp: 620, q: 1.1, noise: 0.09, lfo: 0.33 },
  // a bearing, barely there
  roulette: { osc: 84, wave: 'sine', bp: 1500, q: 0.6, noise: 0.06, lfo: 0.11 },
  // the claw gantry's transformer, the loudest hum in the arcade
  greifer: { osc: 100, wave: 'square', bp: 420, q: 1.2, noise: 0.11, lfo: 0.24 },
};

export class Audio {
  private ctx: WCtx | null = null;
  private clock = 0;
  private on: boolean;
  private vol: number;
  private disposed = false;

  private master: WGain | null = null;
  private buses: Record<Bus, WGain> | null = null;
  private beds: Beds | null = null;
  private bufs = new Map<string, WBuffer>();

  /** the cue currently being synthesised. Recipes are synchronous and never re-enter. */
  private cur: { dest: WGain; srcs: Stoppable[] } | null = null;

  private voices: Voice[] = [];
  /** voice gain nodes waiting out their grace period before being unhooked from the bus */
  private reap: { node: WGain; at: number }[] = [];
  private heat = new Map<string, { heat: number; at: number }>();
  private spectacle: { id: string; priority: number; until: number } | null = null;
  private counters = { played: 0, stolen: 0, ducked: 0 };

  private flickerAt = 0;
  private flickerRng = rng(0x4e0e5157);
  /** the machine the *bed* is currently tuned to. Only `update()` may change it — `push()`
   *  used to set it too, which silently meant the bed never retuned when a frame delivered
   *  events before its update. */
  private machine: string | null = null;
  private unlocking: Promise<void> | null = null;

  constructor(opts: AudioOpts = {}) {
    this.on = opts.enabled !== false;
    this.vol = clamp(opts.volume ?? 0.8, 0, 2);
    // Deliberately no AudioContext here. Constructing one outside a user gesture makes
    // Chrome log, and a headless page has no gesture at all — see unlock().
  }

  get enabled(): boolean { return this.on; }

  // -------------------------------------------------------------- lifecycle

  /**
   * Create and resume the context. Must be called from a user gesture; safe to call again
   * on every click forever (browsers re-suspend on tab switch, so the shell should).
   */
  async unlock(): Promise<void> {
    if (this.disposed) return;
    if (this.unlocking) return this.unlocking;
    // Assign the in-flight promise *before* awaiting it, and clear it outside the worker.
    // The first version cleared it in the worker's own `finally`, which for the synchronous
    // paths (no Web Audio at all, or a context already running) ran *before* the assignment
    // — so `unlocking` stayed set forever and every later click was a no-op. Exactly the
    // bug that makes audio "work once, then never again after a tab switch".
    const p = this.doUnlock();
    this.unlocking = p;
    try { await p; } finally { if (this.unlocking === p) this.unlocking = null; }
  }

  private async doUnlock(): Promise<void> {
    try {
      if (!this.ctx) {
        const Ctor = audioCtor();
        if (!Ctor) return;               // no Web Audio: remain a silent, counting sink
        const ctx = new Ctor({ latencyHint: 'interactive' });
        this.ctx = ctx;
        this.build(ctx);
        // The clock's origin just changed from our own counter to ctx.currentTime, so
        // every timestamp in the ducker and the voice list is now meaningless. Clear.
        this.heat.clear();
        this.voices.length = 0;
        this.reap.length = 0;
        this.spectacle = null;
        this.flickerAt = ctx.currentTime;
      }
      const ctx = this.ctx;
      if (ctx && ctx.state !== 'running') await ctx.resume();
    } catch {
      // A refused or unsupported context is not an error worth a console line, and a
      // console line here fails the E2E gate. Fall back to silence and keep counting.
      this.ctx = null;
      this.master = null;
      this.buses = null;
      this.beds = null;
    }
  }

  setEnabled(v: boolean): void {
    this.on = v;
    try {
      const ctx = this.ctx;
      if (ctx && this.master) {
        const t = ctx.currentTime;
        this.master.gain.cancelScheduledValues(t);
        this.master.gain.setValueAtTime(this.master.gain.value, t);
        this.master.gain.linearRampToValueAtTime(v ? this.vol * MASTER_HEADROOM : 0, t + SNAP);
      }
    } catch { /* muting must never be able to fail loudly */ }
  }

  setVolume(v: number): void {
    this.vol = clamp(v, 0, 2);
    this.setEnabled(this.on);
  }

  /**
   * `played` counts cues that survived the mix policy, whether or not there was a context
   * to hear them on — so a headless soak measures exactly the same policy the browser runs.
   * `stolen` counts both directions of the voice cap: an old voice cut short *and* a new
   * one refused. `ducked` counts cues the repeat ducker attenuated.
   */
  stats(): { voices: number; played: number; stolen: number; ducked: number } {
    try { this.prune(this.now()); } catch { /* diagnostics must not be able to throw */ }
    return { voices: this.voices.length, played: this.counters.played, stolen: this.counters.stolen, ducked: this.counters.ducked };
  }

  dispose(): void {
    this.disposed = true;
    const ctx = this.ctx;
    this.ctx = null;
    this.master = null;
    this.buses = null;
    this.beds = null;
    this.voices.length = 0;
    this.reap.length = 0;
    this.heat.clear();
    this.bufs.clear();
    try { void ctx?.close(); } catch { /* already closed */ }
  }

  // -------------------------------------------------------------- the feed

  /** Feed one frame's events. Never throws, before or after unlock, context or not. */
  push(events: readonly SimEvent[], ctx: { phase: string; machine: string | null }): void {
    if (this.disposed) return;
    try {
      // the cabinet only seeds the jitter here; the bed's own tuning belongs to update()
      const seed = ctx.machine ? strHash(ctx.machine) : 0;
      const now = this.now();
      this.prune(now);
      let collisions = 0;
      for (const ev of events) {
        if (ev.t === 'collision' && ++collisions > mixPolicy.COLLISION_PER_FRAME) continue;
        const cue = soundFor(ev, seed);
        if (cue) this.fire(cue, now);
      }
    } catch { /* a sink that can break the frame loop is worse than a sink that is silent */ }
  }

  /** Per-frame: beds, the neon's FLICKER steps, voice reaping, spectacle release. */
  update(dt: number, ctx: { phase: string; machine: string | null; tilt01: number; topf01: number }): void {
    if (this.disposed) return;
    try {
      // A tab that was backgrounded for a minute must not schedule a minute of flicker.
      const d = clamp(dt, 0, 0.25);
      this.clock += d;
      const now = this.now();
      this.prune(now);

      if (this.spectacle && now >= this.spectacle.until) {
        this.spectacle = null;
        this.unduck(now);
      }

      const beds = this.beds;
      const actx = this.ctx;
      if (!actx || !beds) return;

      const lvl = PHASE_BED[ctx.phase] ?? PHASE_BED.aisle;
      // The bed cross-fades over exactly the camera's dolly time, so the room changes size
      // at the same rate the picture does. tc = DOLLY/3 puts ~95% of the move inside DOLLY.
      const tc = DOLLY / 3;
      beds.roomGain.gain.setTargetAtTime(BED_LEVEL.room * lvl.room, now, tc);
      beds.fanGain.gain.setTargetAtTime(BED_LEVEL.fan * lvl.room, now, tc);
      beds.neonGain.gain.setTargetAtTime(BED_LEVEL.neon * lvl.neon, now, tc);

      if (ctx.machine !== this.machine) {
        this.machine = ctx.machine;
        this.retuneMachineBed(beds, now);
      }
      beds.machGain.gain.setTargetAtTime(BED_LEVEL.mach * lvl.mach * (ctx.machine ? 1 : 0.55), now, tc);

      // TILT is a spring being wound: a strained partial that rises in pitch, squared so it
      // stays out of the way until the meter is genuinely dangerous.
      const t01 = clamp(ctx.tilt01, 0, 1);
      beds.strainGain.gain.setTargetAtTime(BED_LEVEL.strain * t01 * t01, now, SNAP);
      beds.strainOsc.frequency.setTargetAtTime(190 + t01 * 240, now, SETTLE);

      // A full TOPF is heavy. Its hum is the cabinet's own resonance, not a warning.
      const p01 = clamp(ctx.topf01, 0, 1);
      beds.topfGain.gain.setTargetAtTime(BED_LEVEL.topf * p01, now, SETTLE);
      beds.topfOsc.frequency.setTargetAtTime(74 + p01 * 26, now, SETTLE);

      this.stepFlicker(beds, now);
    } catch { /* see push() */ }
  }

  // -------------------------------------------------------------- mix policy, applied

  private now(): number { return this.ctx ? this.ctx.currentTime : this.clock; }

  /**
   * A voice's slot expires at `endsAt`, but its gain node is unhooked from the bus a third
   * of a second later. `endsAt` is a policy number (how long this cue owns a slot) and the
   * recipes are hand-timed, so the two can disagree by a few milliseconds; disconnecting on
   * the exact tick chops the tail and clicks. The grace costs one dead gain node per cue for
   * a third of a second and removes a whole class of bug.
   */
  private prune(now: number): void {
    for (let i = this.voices.length - 1; i >= 0; i--) {
      const v = this.voices[i];
      if (v.endsAt > now) continue;
      this.voices.splice(i, 1);
      if (v.node) this.reap.push({ node: v.node, at: now + 0.35 });
    }
    for (let i = this.reap.length - 1; i >= 0; i--) {
      if (this.reap[i].at > now) continue;
      try { this.reap[i].node.disconnect(); } catch { /* already gone */ }
      this.reap.splice(i, 1);
    }
  }

  /** The three rules, in order: duck the repeat, cap the voices, then synthesise. */
  private fire(cue: Cue, now: number): void {
    // 1. repeat ducker -------------------------------------------------------------
    const h = this.heat.get(cue.id);
    const heat = h ? mixPolicy.decayHeat(h.heat, now - h.at) : 0;
    this.heat.set(cue.id, { heat: heat + 1, at: now });
    const duck = mixPolicy.repeatGain(heat);
    if (duck < 1) this.counters.ducked++;

    // 2. pitch variation -----------------------------------------------------------
    const cents = mixPolicy.jitter(cue.seed, cue.house ? mixPolicy.HOUSE_JITTER_CENTS : mixPolicy.JITTER_CENTS)
      + mixPolicy.repeatDetune(heat);

    // 3. one spectacle at a time ---------------------------------------------------
    let bus = cue.bus;
    let gain = cue.gain * duck;
    const heldBefore = this.spectacle;
    if (bus === 'spectacle') {
      if (mixPolicy.spectacleAction(this.spectacle, cue, now) === 'take') {
        this.spectacle = { id: cue.id, priority: cue.priority, until: now + cue.dur };
        this.duckFor(now);
      } else {
        bus = 'machine';
        gain *= mixPolicy.SPECTACLE_DEMOTE_GAIN;
      }
    }

    // 4. voice cap -----------------------------------------------------------------
    const incoming: VoiceRank = { priority: cue.priority, amp: gain, startedAt: now };
    if (this.voices.length >= mixPolicy.VOICE_CAP) {
      const idx = mixPolicy.victim(this.voices, incoming);
      this.counters.stolen++;
      if (idx < 0) {
        // Refused. If it had just claimed the room, give the room back — ducking everything
        // for a spectacle that never plays is the worst outcome available here.
        if (this.spectacle !== heldBefore) {
          this.spectacle = heldBefore;
          this.unduck(now);
        }
        return;
      }
      this.cut(this.voices[idx], now);
      this.voices.splice(idx, 1);
    }

    this.counters.played++;
    const voice: Voice = { id: cue.id, bus, priority: cue.priority, amp: gain, startedAt: now, endsAt: now + cue.dur, node: null, srcs: [] };
    this.voices.push(voice);

    const ctx = this.ctx;
    const buses = this.buses;
    if (!ctx || !buses || !this.on) return;      // headless or muted: bookkeeping only

    const dest = ctx.createGain();
    dest.gain.value = 1;
    dest.connect(buses[bus]);
    voice.node = dest;
    this.cur = { dest, srcs: voice.srcs };
    try {
      this.render(cue, Math.max(now, ctx.currentTime), gain, cents);
    } finally {
      this.cur = null;
    }
  }

  /** Cut a stolen voice over 8 ms — long enough not to click, short enough not to be heard. */
  private cut(v: Voice, now: number): void {
    try {
      if (v.node) {
        v.node.gain.cancelScheduledValues(now);
        v.node.gain.setValueAtTime(v.node.gain.value, now);
        v.node.gain.linearRampToValueAtTime(0, now + 0.008);
      }
      for (const s of v.srcs) s.stop(now + 0.01);
    } catch { /* a source that already ended throws on stop in some engines */ }
  }

  private duckFor(now: number): void {
    const buses = this.buses;
    if (!buses) return;
    for (const b of ['ambient', 'machine', 'ui'] as const) {
      const g = buses[b].gain;
      g.cancelScheduledValues(now);
      g.setValueAtTime(g.value, now);
      g.linearRampToValueAtTime(mixPolicy.SPECTACLE_DUCK[b], now + SNAP);
    }
  }

  private unduck(now: number): void {
    const buses = this.buses;
    if (!buses) return;
    for (const b of ['ambient', 'machine', 'ui'] as const) {
      const g = buses[b].gain;
      g.cancelScheduledValues(now);
      g.setValueAtTime(g.value, now);
      // the room comes back over SETTLE, easing, because getting the room back is luck
      g.setTargetAtTime(1, now, SETTLE / 3);
    }
  }

  // -------------------------------------------------------------- graph construction

  private build(ctx: WCtx): void {
    // buses -> master -> limiter -> out. The limiter is last so it sees the sum; a coin
    // rain landing on top of a bankBreak is exactly the moment the mix needs a ceiling.
    // MEASURED, and not what I expected. `DynamicsCompressorNode` is a poor brickwall for a
    // game made of transients: rendered offline, a 45 ms coin ping peaking at 0.24 came out
    // at **0.057** through threshold −4 / ratio 12 / attack 3 ms — a 4× hit on a signal
    // twelve dB *below* threshold — while the same settings let a ping at 1.6 through at
    // 0.35. In other words it flattened every coin in the game and did not actually stop
    // anything getting loud. The attenuation tracks the attack time and is nearly
    // level-independent, so a short attack is not "safer", it is just duller.
    //
    // So it is set as a gentle bus compressor: it controls *sustained* level (the chute
    // break's roar, the bed sum, a shower landing on a spectacle) and leaves coin
    // transients alone (measured ×0.66 rather than ×0.23). Peak safety comes from gain
    // staging and the voice cap instead, which is where it belonged all along.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 6;
    limiter.ratio.value = 4;
    limiter.attack.value = 0.020;
    limiter.release.value = 0.25;   // slow enough not to pump on a shower
    limiter.connect(ctx.destination);

    const master = ctx.createGain();
    master.gain.value = this.on ? this.vol * MASTER_HEADROOM : 0;
    master.connect(limiter);
    this.master = master;

    const mk = (): WGain => { const g = ctx.createGain(); g.gain.value = 1; g.connect(master); return g; };
    this.buses = { ambient: mk(), machine: mk(), spectacle: mk(), ui: mk() };

    this.beds = this.buildBeds(ctx, this.buses.ambient);
  }

  /**
   * The two beds that never stop, plus the machine bed. Everything here starts at once and
   * runs for the life of the context: continuous sources are cheaper than re-triggering,
   * and a room tone that restarts is a room tone you can hear restarting.
   */
  private buildBeds(ctx: WCtx, dest: WGain): Beds {
    // Everything continuous goes through one fade node that comes up from silence over
    // BEAT×2. Nine oscillators starting at phase zero on the same sample sum coherently,
    // and the first version of this thumped audibly the instant you clicked to unlock —
    // measured at 0.20 peak in an offline render, as loud as a coin hitting the tray. The
    // arcade should come *on*, not bang. update() never touches this node.
    const out = ctx.createGain();
    out.gain.setValueAtTime(0.0001, ctx.currentTime);
    out.gain.linearRampToValueAtTime(1, ctx.currentTime + BEAT * 2);
    out.connect(dest);

    // --- room tone: 8 s of seam-crossfaded brown noise through a gentle lowpass. Eight
    // seconds because anything shorter has an identifiable loop period, and DRIFT (3.2 s)
    // is the shortest cycle the eye is allowed to notice, so the ear gets more than twice
    // that. Q stays under 1.2 everywhere in the bed: a resonant peak is what makes an
    // ambience unbearable at minute nine, not its level.
    const roomBuf = this.buffer(ctx, 'brown', 8, 0xa17c0de, true);
    const roomSrc = ctx.createBufferSource();
    roomSrc.buffer = roomBuf;
    roomSrc.loop = true;
    roomSrc.loopStart = 0;
    roomSrc.loopEnd = roomBuf ? roomBuf.duration : 8;
    roomSrc.playbackRate.value = 0.92;
    const roomLp = ctx.createBiquadFilter();
    roomLp.type = 'lowpass';
    roomLp.frequency.value = 380;
    roomLp.Q.value = 0.6;
    const roomGain = ctx.createGain();
    roomGain.gain.value = BED_LEVEL.room;
    roomSrc.connect(roomLp);
    roomLp.connect(roomGain);
    roomGain.connect(out);
    roomSrc.start();

    // --- the distant fan: a low tone with a very slow wobble. Its period is DRIFT×4, so it
    // is never in phase with anything you can see, which is what keeps it "the building".
    const fanOsc = ctx.createOscillator();
    fanOsc.type = 'sine';
    fanOsc.frequency.value = 61.5;
    const fanGain = ctx.createGain();
    fanGain.gain.value = BED_LEVEL.fan;
    fanOsc.connect(fanGain);
    fanGain.connect(out);
    fanOsc.start();
    const fanLfo = ctx.createOscillator();
    fanLfo.type = 'sine';
    fanLfo.frequency.value = 1 / (DRIFT * 4);
    const fanLfoGain = ctx.createGain();
    fanLfoGain.gain.value = 1.6;
    fanLfo.connect(fanLfoGain);
    fanLfoGain.connect(fanOsc.frequency);
    fanLfo.start();

    // --- neon buzz. This is Germany: mains is 50 Hz, so the *audible* hum is 100 Hz (the
    // ballast rectifies, and you hear twice mains), with 50 Hz felt underneath and odd
    // harmonics from the choke. Four partials and a whisper of gas hiss.
    const neonGain = ctx.createGain();
    neonGain.gain.value = BED_LEVEL.neon;
    const neonFlicker = ctx.createGain();
    neonFlicker.gain.value = 1;
    neonFlicker.connect(neonGain);
    neonGain.connect(out);
    const neonOscs: WOsc[] = [];
    for (const [f, a] of [[50, 0.35], [100, 1.0], [150, 0.30], [250, 0.13]] as [number, number][]) {
      const o = ctx.createOscillator();
      o.type = f === 100 ? 'triangle' : 'sine';
      o.frequency.value = f;
      const g = ctx.createGain();
      g.gain.value = a;
      o.connect(g);
      g.connect(neonFlicker);
      o.start();
      neonOscs.push(o);
    }
    const hissBuf = this.buffer(ctx, 'white', 4, 0x6a5, true);
    const neonHiss = ctx.createBufferSource();
    neonHiss.buffer = hissBuf;
    neonHiss.loop = true;
    const hissBp = ctx.createBiquadFilter();
    hissBp.type = 'bandpass';
    hissBp.frequency.value = 3800;
    hissBp.Q.value = 1.1;
    const hissGain = ctx.createGain();
    hissGain.gain.value = 0.05;
    neonHiss.connect(hissBp);
    hissBp.connect(hissGain);
    hissGain.connect(neonFlicker);
    neonHiss.start();

    // --- machine bed: one oscillator, one filtered noise band, one LFO. Retuned per
    // cabinet rather than rebuilt, so walking the aisle is a crossfade and not a series of
    // switch-ons. The LFO is a TRIANGLE — linear ramps, House machinery, §7.2 law 3.
    const machGain = ctx.createGain();
    machGain.gain.value = 0;
    machGain.connect(out);
    const machOsc = ctx.createOscillator();
    machOsc.type = 'triangle';
    machOsc.frequency.value = 100;
    const machOscGain = ctx.createGain();
    machOscGain.gain.value = 0.6;
    machOsc.connect(machOscGain);
    machOscGain.connect(machGain);
    machOsc.start();
    const machNoise = ctx.createBufferSource();
    machNoise.buffer = this.buffer(ctx, 'brown', 6, 0x9ea7, true);
    machNoise.loop = true;
    const machBp = ctx.createBiquadFilter();
    machBp.type = 'bandpass';
    machBp.frequency.value = 400;
    machBp.Q.value = 0.9;
    const machNoiseGain = ctx.createGain();
    machNoiseGain.gain.value = 0.1;
    machNoise.connect(machBp);
    machBp.connect(machNoiseGain);
    machNoiseGain.connect(machGain);
    machNoise.start();
    const machLfo = ctx.createOscillator();
    machLfo.type = 'triangle';
    machLfo.frequency.value = 0.3;
    const machLfoGain = ctx.createGain();
    machLfoGain.gain.value = 90;
    machLfo.connect(machLfoGain);
    machLfoGain.connect(machBp.frequency);
    machLfo.start();

    // --- TILT strain and TOPF weight: two tiny oscillators driven straight from update().
    const strainOsc = ctx.createOscillator();
    strainOsc.type = 'sawtooth';
    strainOsc.frequency.value = 190;
    const strainLp = ctx.createBiquadFilter();
    strainLp.type = 'lowpass';
    strainLp.frequency.value = 900;
    strainLp.Q.value = 1.0;
    const strainGain = ctx.createGain();
    strainGain.gain.value = 0;
    strainOsc.connect(strainLp);
    strainLp.connect(strainGain);
    strainGain.connect(out);
    strainOsc.start();

    const topfOsc = ctx.createOscillator();
    topfOsc.type = 'sine';
    topfOsc.frequency.value = 74;
    const topfGain = ctx.createGain();
    topfGain.gain.value = 0;
    topfOsc.connect(topfGain);
    topfGain.connect(out);
    topfOsc.start();

    return {
      fade: out,
      roomSrc, roomLp, roomGain, fanOsc, fanGain,
      neonGain, neonFlicker, neonOscs, neonHiss,
      machGain, machOsc, machNoise, machBp, machLfo, machLfoGain,
      strainOsc, strainGain, topfOsc, topfGain,
    };
  }

  private retuneMachineBed(beds: Beds, now: number): void {
    const m = (this.machine && MACHINE_BED[this.machine]) || MACHINE_BED.pachinko;
    const tc = DOLLY / 3;
    beds.machOsc.type = m.wave;
    beds.machOsc.frequency.setTargetAtTime(m.osc, now, tc);
    beds.machBp.frequency.setTargetAtTime(m.bp, now, tc);
    beds.machBp.Q.setTargetAtTime(m.q, now, tc);
    beds.machLfo.frequency.setTargetAtTime(m.lfo, now, tc);
    beds.machLfoGain.gain.setTargetAtTime(m.bp * 0.22, now, tc);
  }

  /**
   * The neon's amplitude is **stepped**, not smoothed: one value per FLICKER (70 ms), held.
   * That is the bible's flicker quantum applied to sound, and it is the difference between
   * a tube and a synthesiser pad. Once in a while the tube stalls for two steps — the sign
   * gag is canon and the "a" is dying, so the buzz should be dying with it, on its own
   * schedule, never cued.
   */
  private stepFlicker(beds: Beds, now: number): void {
    if (this.flickerAt < now) this.flickerAt = now;
    let guard = 0;
    while (this.flickerAt < now + 0.4 && guard++ < 40) {
      const r = this.flickerRng();
      const sick = r < 0.028;
      const level = sick ? 0.15 : 0.82 + r * 0.24;
      beds.neonFlicker.gain.setValueAtTime(level, this.flickerAt);
      this.flickerAt += FLICKER * (sick ? 2 : 1);
    }
  }

  // -------------------------------------------------------------- synth primitives

  /**
   * One enveloped oscillator into the current voice.
   *
   * `glide` is the motion doctrine: `'house'` ramps frequency exponentially (a constant
   * number of semitones per second — a dead straight glide in the domain the ear hears)
   * and fades amplitude on a straight line, so the sound stops rather than dies away.
   * `'luck'` overshoots the target pitch by 8% and settles back over the tail, and decays
   * exponentially. It is genuinely audible, and it is free.
   */
  private tone(o: {
    t: number; dur: number; amp: number; f0: number; f1?: number;
    type?: WaveName; attack?: number; detune?: number; glide?: 'house' | 'luck';
    filter?: { type: string; f0: number; f1?: number; q?: number };
  }): void {
    const ctx = this.ctx;
    const cur = this.cur;
    if (!ctx || !cur || o.amp <= 0 || o.dur <= 0) return;
    const t = Math.max(o.t, ctx.currentTime);
    const osc = ctx.createOscillator();
    osc.type = o.type ?? 'sine';
    osc.frequency.setValueAtTime(Math.max(8, o.f0), t);
    osc.detune.value = o.detune ?? 0;
    const end = t + o.dur;
    if (o.f1 !== undefined && o.f1 !== o.f0) {
      const f1 = Math.max(8, o.f1);
      if ((o.glide ?? 'luck') === 'house') {
        osc.frequency.exponentialRampToValueAtTime(f1, end);
      } else {
        // overshoot 8% then settle — the pitch equivalent of ease.outBack
        osc.frequency.exponentialRampToValueAtTime(Math.max(8, f1 * (o.f1 > o.f0 ? 1.08 : 0.93)), t + o.dur * 0.62);
        osc.frequency.exponentialRampToValueAtTime(f1, end);
      }
    }

    let tail: WNode = osc;
    if (o.filter) {
      const bq = ctx.createBiquadFilter();
      bq.type = o.filter.type;
      bq.frequency.setValueAtTime(Math.max(20, o.filter.f0), t);
      bq.Q.value = o.filter.q ?? 1;
      if (o.filter.f1 !== undefined) bq.frequency.exponentialRampToValueAtTime(Math.max(20, o.filter.f1), end);
      osc.connect(bq);
      tail = bq;
    }

    const g = ctx.createGain();
    const atk = Math.min(o.attack ?? 0.004, o.dur * 0.5);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, o.amp), t + atk);
    if ((o.glide ?? 'luck') === 'house') g.gain.linearRampToValueAtTime(0.0001, end);
    else g.gain.exponentialRampToValueAtTime(0.0001, end);
    g.gain.setValueAtTime(0, end);
    tail.connect(g);
    g.connect(cur.dest);

    osc.start(t);
    osc.stop(end + 0.01);
    cur.srcs.push(osc);
    osc.onended = (): void => { try { osc.disconnect(); g.disconnect(); if (tail !== osc) tail.disconnect(); } catch { /* gone */ } };
  }

  /** One enveloped noise burst through a swept filter. The transient half of everything. */
  private noise(o: {
    kind: 'white' | 'pink' | 'brown'; t: number; dur: number; amp: number;
    f0: number; f1?: number; type?: string; q?: number; attack?: number; rate?: number; seed?: number;
  }): void {
    const ctx = this.ctx;
    const cur = this.cur;
    if (!ctx || !cur || o.amp <= 0 || o.dur <= 0) return;
    const t = Math.max(o.t, ctx.currentTime);
    // ONE two-second buffer per noise colour, shared by every burst in the game. The first
    // version of this seeded a fresh buffer per call, which allocated ~380 kB per coin tick
    // and would have eaten a gigabyte inside a floor. Variation comes from the read offset
    // below instead, which costs nothing.
    const buf = this.buffer(ctx, o.kind, 2, 0x51ee, false);
    if (!buf) return;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.playbackRate.value = o.rate ?? 1;
    const bq = ctx.createBiquadFilter();
    bq.type = o.type ?? 'bandpass';
    const end = t + o.dur;
    bq.frequency.setValueAtTime(Math.max(20, o.f0), t);
    bq.Q.value = o.q ?? 1;
    if (o.f1 !== undefined) bq.frequency.exponentialRampToValueAtTime(Math.max(20, o.f1), end);
    const g = ctx.createGain();
    const atk = Math.min(o.attack ?? 0.002, o.dur * 0.5);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, o.amp), t + atk);
    g.gain.exponentialRampToValueAtTime(0.0001, end);
    g.gain.setValueAtTime(0, end);
    src.connect(bq);
    bq.connect(g);
    g.connect(cur.dest);
    // read from a per-call offset so two bursts are never literally the same noise. The
    // offset comes from the call's own numbers, not from the clock, so a replay is identical.
    const off = hash32((o.seed ?? 0) ^ Math.round(o.f0) ^ Math.round(o.dur * 10007)) / 4294967296;
    src.start(t, off * 1.5);
    src.stop(end + 0.01);
    cur.srcs.push(src);
    src.onended = (): void => { try { src.disconnect(); bq.disconnect(); g.disconnect(); } catch { /* gone */ } };
  }

  /**
   * A coin. A small free disc rings inharmonically — its first plate modes sit near
   * 1 : 2.71 : 5.15, which is why a coin sounds like a coin and a sine sounds like a
   * doorbell. `full` drops the upper partials for pings buried inside a dense shower,
   * where they are masked anyway: a real economy, worth about 40% of the shower's cost.
   */
  private ping(t: number, f: number, amp: number, decay: number, full = true, detune = 0): void {
    this.tone({ type: 'triangle', f0: f, t, dur: decay, amp: amp * 0.9, attack: 0.001, detune });
    this.tone({ type: 'sine', f0: f * 2.71, t, dur: decay * 0.62, amp: amp * 0.5, attack: 0.001, detune });
    if (!full) return;
    this.tone({ type: 'sine', f0: f * 5.15, t, dur: decay * 0.34, amp: amp * 0.24, attack: 0.001, detune });
    this.noise({ kind: 'white', t, dur: 0.006, amp: amp * 0.45, type: 'bandpass', f0: f * 2.2, q: 1.1, seed: Math.round(f) });
  }

  /** A low impact. Pitch drops fast; that drop is the whole difference between a thud and a beep. */
  private thud(t: number, f: number, amp: number, decay: number, glide: 'house' | 'luck' = 'house'): void {
    this.tone({ type: 'sine', f0: f, f1: f * 0.55, t, dur: decay, amp, attack: 0.002, glide });
    this.noise({ kind: 'brown', t, dur: decay * 0.5, amp: amp * 0.5, type: 'lowpass', f0: f * 6, q: 0.8, seed: Math.round(f * 3) });
  }

  /**
   * THE signature sound: coin rain that scales with payout mass.
   *
   * Many short metallic pings scattered over a window, front-loaded (`u^1.6`) because coins
   * arrive in a burst and then trail — a uniform scatter sounds like a metronome made of
   * money. The shower detunes ~22% downward across its own window: the tray fills and the
   * pile damps its own ring, and that descent is what makes a big payout sound like it is
   * *landing* rather than merely lasting.
   */
  private rain(t0: number, mass: number, seed: number, gain: number, bright = 1): number {
    const { count, window, ping } = coinRainShape(mass);
    const r = rng(seed);
    for (let i = 0; i < count; i++) {
      const at = t0 + window * Math.pow(r(), 1.6);
      const fall = 1 - 0.22 * ((at - t0) / Math.max(1e-3, window));
      const f = 1750 * bright * fall * (0.72 + r() * 0.85);
      this.ping(at, f, gain * ping * (0.8 + r() * 0.45), 0.05 + r() * 0.09, i < 16);
    }
    // the tray itself answers: one low resonance under the whole shower
    this.thud(t0 + 0.01, 96, gain * 0.28, window * 0.7 + 0.2);
    return window;
  }

  // -------------------------------------------------------------- the recipes

  private render(cue: Cue, t: number, g: number, cents: number): void {
    const r = rng(cue.seed);
    switch (cue.id) {
      // ---- coins -------------------------------------------------------------
      case 'coin.launch':
        // the coin leaving the slot: a bright scrape up the chute, then the mechanism swallowing
        this.noise({ kind: 'white', t, dur: 0.07, amp: g * 0.5, type: 'bandpass', f0: 1500, f1: 3600, q: 2.2, seed: cue.seed });
        this.ping(t + 0.05, 2400 * centsMul(cents), g * 0.55, 0.09);
        this.thud(t + 0.06, 150, g * 0.3, 0.12);
        break;

      case 'coin.tick':
        this.ping(t, (cue.freq ?? 1800) * centsMul(cents), g, 0.045, false);
        break;

      case 'coin.rain':
      case 'cash.out':
        this.rain(t, cue.mass ?? 20, cue.seed, g, centsMul(cents));
        break;

      // ---- scoring -----------------------------------------------------------
      case 'score.pling': {
        const tags = cue.detail ?? '';
        const f = (cue.freq ?? 620) * centsMul(cents);
        // tags ride as timbre, never as extra voices: [Gold] brightens, [Fluch] detunes flat,
        // [Feuer] adds a hiss. Three tags on one coin must compose, not fight (ART-BIBLE §4).
        const gold = tags.includes('Gold');
        const fluch = tags.includes('Fluch');
        this.tone({ type: gold ? 'triangle' : 'sine', f0: f, f1: f * 1.5, t, dur: SETTLE, amp: g, attack: 0.003, glide: 'luck', detune: fluch ? -55 : 0 });
        this.tone({ type: 'sine', f0: f * (fluch ? 2.38 : 2.51), t, dur: SETTLE * 0.5, amp: g * (gold ? 0.5 : 0.28), attack: 0.002 });
        if (tags.includes('Feuer')) this.noise({ kind: 'white', t, dur: SETTLE * 0.7, amp: g * 0.18, type: 'bandpass', f0: 2600, f1: 5200, q: 0.9, seed: cue.seed });
        break;
      }

      case 'stage.step':
        // the pipeline showing its working: one ledger key per stage, on the grid
        this.noise({ kind: 'white', t, dur: 0.018, amp: g * 0.7, type: 'bandpass', f0: 2400 * centsMul(cents), q: 2.4, seed: cue.seed });
        this.tone({ type: 'square', f0: 420 * centsMul(cents), t, dur: RATCHET, amp: g * 0.3, glide: 'house' });
        break;

      // ---- the TOPF ----------------------------------------------------------
      case 'topf.slurp': {
        // a descending gulp: the near-miss being banked. Pitch falls a fifth and a half, the
        // lowpass closes behind it, and the "gulp" is the click of the flap at the bottom.
        const f = 520 * centsMul(cents);
        this.tone({ type: 'triangle', f0: f, f1: 90, t, dur: SETTLE, amp: g * 0.8, attack: 0.01, glide: 'house', filter: { type: 'lowpass', f0: 2200, f1: 340, q: 1.2 } });
        this.noise({ kind: 'brown', t, dur: SETTLE * 0.8, amp: g * 0.3, type: 'bandpass', f0: 700, f1: 180, q: 1.4, seed: cue.seed });
        this.thud(t + SETTLE * 0.86, 120, g * 0.5, 0.1);
        break;
      }

      // ---- spectacles --------------------------------------------------------
      case 'jackpot.fanfare': {
        // Never Vegas. A struck bell and a three-note chiptune arpeggio, played straight —
        // the cheapness IS the joke, and the joke is not commented on (ART-BIBLE §0).
        const f = 880 * centsMul(cents);
        this.tone({ type: 'sine', f0: f, t, dur: SLOWMO, amp: g * 0.55, attack: 0.002 });
        this.tone({ type: 'sine', f0: f * 2.76, t, dur: SLOWMO * 0.7, amp: g * 0.3, attack: 0.002 });
        this.tone({ type: 'sine', f0: f * 5.4, t, dur: SLOWMO * 0.35, amp: g * 0.16, attack: 0.002 });
        for (let i = 0; i < 3; i++) {
          this.tone({ type: 'square', f0: f * 0.5 * Math.pow(2, i / 3), t: t + BEAT * 0.5 + i * RATCHET * 2, dur: RATCHET * 3, amp: g * 0.22, glide: 'luck' });
        }
        this.rain(t + BEAT, cue.mass ?? 100, cue.seed ^ 0x1a, g * 0.9);
        break;
      }

      case 'bank.break': {
        // The money shot. ANTIC of intake first — nothing in this game starts instantly —
        // then a break that is genuinely different per mode, then the coins.
        this.noise({ kind: 'white', t, dur: ANTIC, amp: g * 0.28, type: 'bandpass', f0: 300, f1: 2400, q: 1.0, attack: ANTIC * 0.9, seed: cue.seed });
        const b = t + ANTIC;
        this.break_(cue.detail as BankBreakMode | undefined, b, g, cue.seed, r);
        this.rain(b + SETTLE * 0.6, cue.mass ?? 500, cue.seed ^ 0x5b, g);
        break;
      }

      case 'abrechnung.ledger': {
        // Der Croupier's adding machine. Six key clunks at exactly RATCHET×2, then the
        // carriage. Strictly on the grid: this is the House counting your money.
        const n = Math.min(cue.reps ?? 6, 12);
        for (let i = 0; i < n; i++) {
          const at = t + i * RATCHET * 2;
          this.noise({ kind: 'white', t: at, dur: 0.02, amp: g * 0.5, type: 'bandpass', f0: 1900, q: 2.0, seed: cue.seed + i });
          this.thud(at, 160, g * 0.32, 0.07);
        }
        const car = t + n * RATCHET * 2 + BEAT * 0.4;
        this.noise({ kind: 'white', t: car, dur: SETTLE * 0.6, amp: g * 0.4, type: 'bandpass', f0: 900, f1: 2600, q: 1.1, seed: cue.seed ^ 7 });
        this.thud(car + SETTLE * 0.55, 110, g * 0.7, SETTLE);
        break;
      }

      case 'till.print': {
        // RATCHET is one reel detent and one printed line, so the till and the reels are
        // audibly the same mechanism — which is the point: the receipt is a machine too.
        const n = clamp(cue.reps ?? 8, 1, mixPolicy.RECEIPT_MAX_STEPS);
        for (let i = 0; i < n; i++) {
          const at = t + i * RATCHET;
          // stepper-motor buzz: a short square note, same pitch every line (a stepper does
          // not vary) plus the paper being dragged over the platen.
          this.tone({ type: 'square', f0: 330 * centsMul(cents), t: at, dur: 0.020, amp: g * 0.30, attack: 0.001, glide: 'house', filter: { type: 'lowpass', f0: 2600, q: 0.8 } });
          this.noise({ kind: 'white', t: at, dur: 0.026, amp: g * 0.13, type: 'bandpass', f0: 2700, q: 0.7, seed: cue.seed + i });
        }
        // the tear: a rising rip, then the strip landing on the tray
        const tear = t + n * RATCHET + BEAT * 0.5;
        this.noise({ kind: 'white', t: tear, dur: 0.18, amp: g * 0.5, type: 'bandpass', f0: 900, f1: 4400, q: 0.8, attack: 0.03, seed: cue.seed ^ 0x1e });
        this.noise({ kind: 'pink', t: tear + 0.19, dur: 0.12, amp: g * 0.18, type: 'lowpass', f0: 1400, q: 0.7, seed: cue.seed ^ 0x2e });
        break;
      }

      case 'boss.end':
        if (cue.detail === 'win') {
          const f = 330 * centsMul(cents);
          for (let i = 0; i < 3; i++) this.tone({ type: 'triangle', f0: f * Math.pow(2, i * 0.25), f1: f * Math.pow(2, i * 0.25 + 0.02), t: t + i * RATCHET * 3, dur: BEAT, amp: g * 0.4, glide: 'luck' });
        } else {
          // the door closing on you: two straight tones down, no easing, no comment
          this.tone({ type: 'square', f0: 220, f1: 110, t, dur: BEAT, amp: g * 0.35, glide: 'house', filter: { type: 'lowpass', f0: 900, q: 0.9 } });
          this.thud(t + BEAT, 70, g * 0.7, SETTLE);
        }
        break;

      // ---- machine mechanics -------------------------------------------------
      case 'reel.detent': {
        // The detent, matched to ease.detent(): overshoot, hard clunk-back, two after-shivers
        // at 20% and 6%. The picture and the sound shiver on the same schedule.
        const f = 190 * centsMul(cents);
        this.thud(t, f, g, RATCHET * 2);
        this.noise({ kind: 'white', t, dur: 0.012, amp: g * 0.55, type: 'bandpass', f0: 2200, q: 1.6, seed: cue.seed });
        this.thud(t + RATCHET, f * 1.02, g * 0.20, RATCHET * 1.4);
        this.thud(t + RATCHET * 2, f * 1.04, g * 0.06, RATCHET);
        break;
      }

      case 'wheel.settle': {
        // the ball dropping into the pocket and rattling twice before it commits
        const f = 1200 * centsMul(cents);
        this.ping(t, f, g * 0.7, 0.06);
        this.ping(t + 0.055, f * 0.96, g * 0.45, 0.05);
        this.ping(t + 0.10, f * 0.93, g * 0.25, 0.05);
        this.thud(t + 0.13, 130, g * 0.4, SETTLE * 0.7);
        break;
      }

      case 'stakes.up':
        // a lever pulled one notch. House: linear, unsweetened.
        this.noise({ kind: 'white', t, dur: 0.09, amp: g * 0.35, type: 'bandpass', f0: 800, f1: 1800, q: 1.4, seed: cue.seed });
        this.thud(t + 0.09, 180 * centsMul(cents), g * 0.6, SETTLE * 0.6);
        break;

      case 'bust.thud':
        // the machine eating it. One dead weight, then the room noticing.
        this.thud(t, 110 * centsMul(cents), g, SETTLE);
        this.tone({ type: 'sawtooth', f0: 150, f1: 60, t: t + SNAP, dur: BEAT, amp: g * 0.25, glide: 'house', filter: { type: 'lowpass', f0: 700, q: 1.1 } });
        break;

      case 'claw.grab':
        this.noise({ kind: 'white', t, dur: 0.05, amp: g * 0.4, type: 'bandpass', f0: 1200, q: 1.6, seed: cue.seed });
        this.tone({ type: 'triangle', f0: 300 * centsMul(cents), f1: 620, t: t + 0.05, dur: SETTLE, amp: g * 0.5, glide: 'luck' });
        break;

      case 'claw.miss':
        // the claw opening on nothing: servo, then silence where the prize should be
        this.tone({ type: 'sawtooth', f0: 420, f1: 190, t, dur: SETTLE * 0.7, amp: g * 0.3, glide: 'house', filter: { type: 'lowpass', f0: 1400, q: 0.9 } });
        this.noise({ kind: 'white', t: t + SETTLE * 0.6, dur: 0.05, amp: g * 0.2, type: 'lowpass', f0: 900, q: 0.7, seed: cue.seed });
        break;

      // ---- STRÄHNE, TILT, GLÜCK ---------------------------------------------
      case 'straehne.ignite': {
        // it ignites: a rising eased sweep with a gas whoosh under it. Luck eases.
        const step = 1 + clamp((cue.reps ?? 2) - 1, 0, 4) * 0.18;
        this.tone({ type: 'triangle', f0: 220 * step * centsMul(cents), f1: 660 * step, t, dur: SETTLE, amp: g * 0.8, attack: 0.02, glide: 'luck' });
        this.noise({ kind: 'pink', t, dur: SETTLE + 0.15, amp: g * 0.35, type: 'bandpass', f0: 500, f1: 2400, q: 0.8, attack: 0.05, seed: cue.seed });
        break;
      }

      case 'straehne.snuff':
        // the opposite motion, and it must read as the opposite: falling, closing, damp
        this.noise({ kind: 'pink', t, dur: SETTLE, amp: g * 0.45, type: 'lowpass', f0: 2200, f1: 300, q: 0.8, attack: 0.01, seed: cue.seed });
        this.tone({ type: 'triangle', f0: 520 * centsMul(cents), f1: 180, t, dur: SETTLE * 0.8, amp: g * 0.6, glide: 'luck' });
        break;

      case 'tilt.buzz': {
        // A raspberry. The House laughing at you, deadpan: a low buzzy saw through a
        // lowpass with a hard ~28 Hz amplitude flutter, sagging in pitch as it goes.
        const f = 95 * centsMul(cents);
        const flutter = 28;
        const n = Math.round(BEAT * flutter);
        for (let i = 0; i < n; i++) {
          this.tone({ type: 'sawtooth', f0: f * (1 - i / n * 0.18), t: t + i / flutter, dur: 1 / flutter * 0.72, amp: g * 0.5, attack: 0.002, glide: 'house', filter: { type: 'lowpass', f0: 1100, q: 1.1 } });
        }
        break;
      }

      case 'tilt.lockout':
        // a relay dropping out, and the cabinet going quiet under it
        this.noise({ kind: 'white', t, dur: 0.014, amp: g * 0.6, type: 'bandpass', f0: 3000, q: 2.2, seed: cue.seed });
        this.thud(t + 0.01, 80, g * 0.9, SETTLE);
        this.tone({ type: 'square', f0: 240, f1: 120, t: t + SNAP, dur: BEAT * 0.8, amp: g * 0.2, glide: 'house', filter: { type: 'lowpass', f0: 800, q: 0.9 } });
        break;

      case 'glueck.spend': {
        // A bright bent tone. This is the one thing in the game that is purely yours, so it
        // is the most eased sound in the file: the pitch overshoots and settles.
        const f = 880 * centsMul(cents);
        this.tone({ type: 'triangle', f0: f, f1: f * 1.122, t, dur: SETTLE + SNAP, amp: g * 0.6, attack: 0.006, glide: 'luck' });
        this.tone({ type: 'sine', f0: f * 1.5, f1: f * 1.5 * 1.122, t: t + 0.02, dur: SETTLE, amp: g * 0.3, attack: 0.006, glide: 'luck' });
        this.noise({ kind: 'white', t, dur: 0.05, amp: g * 0.2, type: 'highpass', f0: 5200, q: 0.7, seed: cue.seed });
        break;
      }

      case 'glueck.regen': {
        // the pity engine paying out: two soft notes a fourth apart, quiet enough to miss
        const f = 520 * centsMul(cents);
        this.tone({ type: 'sine', f0: f, t, dur: SETTLE * 0.7, amp: g * 0.35, attack: 0.02, glide: 'luck' });
        this.tone({ type: 'sine', f0: f * 1.335, t: t + RATCHET * 3, dur: SETTLE, amp: g * 0.3, attack: 0.02, glide: 'luck' });
        break;
      }

      // ---- tags --------------------------------------------------------------
      case 'tag.split': {
        // one coin cleaving into two: a chink that forks apart in pitch
        const f = 1500 * centsMul(cents);
        this.ping(t, f, g * 0.7, 0.07);
        this.tone({ type: 'triangle', f0: f, f1: f * 1.19, t: t + 0.03, dur: SETTLE * 0.6, amp: g * 0.3, glide: 'luck' });
        this.tone({ type: 'triangle', f0: f, f1: f * 0.84, t: t + 0.03, dur: SETTLE * 0.6, amp: g * 0.3, glide: 'luck' });
        break;
      }

      case 'tag.echo': {
        // literally an echo: three taps at RATCHET×2, each quieter and a tone lower
        const f = (cue.freq ?? 640) * centsMul(cents);
        for (let i = 0; i < (cue.reps ?? 3); i++) {
          this.ping(t + i * RATCHET * 2, f * Math.pow(0.89, i), g * Math.pow(0.55, i), 0.09, i === 0);
        }
        break;
      }

      case 'tag.zins':
        // you can hear the coin double. freq already carries log2(value).
        this.noise({ kind: 'white', t, dur: 0.012, amp: g * 0.5, type: 'bandpass', f0: 3200, q: 2.0, seed: cue.seed });
        this.tone({ type: 'triangle', f0: (cue.freq ?? 380) * centsMul(cents), f1: (cue.freq ?? 380) * 1.06, t, dur: RATCHET * 3, amp: g * 0.9, glide: 'luck' });
        break;

      case 'tag.phase':
        // [Geist] passing through solid matter: a band of noise sliding down and away
        this.noise({ kind: 'pink', t, dur: SETTLE, amp: g * 0.5, type: 'bandpass', f0: 2600, f1: 420, q: 3.2, attack: 0.04, seed: cue.seed });
        break;

      case 'tag.stick':
        // [Kleber]: a wet thup. Short, low, damped — this coin is terrain now.
        this.tone({ type: 'sine', f0: 300 * centsMul(cents), f1: 90, t, dur: SNAP, amp: g * 0.6, attack: 0.002, glide: 'house' });
        this.noise({ kind: 'brown', t, dur: 0.04, amp: g * 0.3, type: 'lowpass', f0: 700, q: 1.2, seed: cue.seed });
        break;

      case 'pile.settle':
        this.thud(t, 120 * centsMul(cents), g, SNAP * 0.8);
        break;

      // ---- the House, walking --------------------------------------------------
      case 'rausschmeisser.step': {
        // Heavy, slow, LINEAR. Steps at exactly BEAT apart — no ritardando, no accelerando,
        // no expressive gap. He is the House. The only variation is HOUSE_JITTER_CENTS,
        // which exists so two identical boots do not comb-filter, not so he can emote.
        const n = cue.reps ?? 4;
        const closing = cue.detail !== 'lose';
        for (let i = 0; i < n; i++) {
          const at = t + i * BEAT;
          const k = closing ? (i + 1) / n : (n - i) / n;   // linear approach / linear retreat
          this.thud(at, 55 * centsMul(cents), g * k, SETTLE * 0.9);
          this.noise({ kind: 'brown', t: at + 0.012, dur: 0.09, amp: g * 0.22 * k, type: 'lowpass', f0: 1100, q: 0.8, seed: cue.seed + i });
        }
        break;
      }

      case 'rausschmeisser.shove':
        // his palm on the glass, and the whole cabinet taking it
        this.thud(t, 68, g, SETTLE);
        this.noise({ kind: 'white', t, dur: 0.05, amp: g * 0.45, type: 'bandpass', f0: 1600, q: 1.2, seed: cue.seed });
        for (let i = 0; i < 5; i++) {
          this.ping(t + 0.05 + i * 0.045 + r() * 0.02, 2600 + r() * 1400, g * 0.14, 0.05, false);
        }
        break;

      case 'rausschmeisser.win':
        // he wins by doing nothing dramatic: one boot, one door, done
        this.thud(t, 52, g, SETTLE);
        this.thud(t + BEAT, 44, g * 0.8, SETTLE * 1.2);
        this.noise({ kind: 'brown', t: t + BEAT + 0.02, dur: 0.14, amp: g * 0.3, type: 'lowpass', f0: 600, q: 0.8, seed: cue.seed });
        break;

      // ---- run structure -----------------------------------------------------
      case 'verdacht.sting':
        // the room noticing you. Cold, brief, and never a horror sting.
        this.tone({ type: 'sine', f0: cue.detail === 'down' ? 300 : 640, f1: cue.detail === 'down' ? 220 : 900, t, dur: SETTLE, amp: g * 0.4, attack: 0.01, glide: 'house', filter: { type: 'bandpass', f0: 1200, q: 1.4 } });
        this.noise({ kind: 'pink', t, dur: SETTLE * 0.6, amp: g * 0.14, type: 'highpass', f0: 3400, q: 0.7, seed: cue.seed });
        break;

      case 'zinken.install':
        // a screwdriver two aisles away. You are not supposed to be sure you heard it.
        for (let i = 0; i < 3; i++) {
          this.noise({ kind: 'white', t: t + i * RATCHET * 2, dur: 0.03, amp: g * 0.4, type: 'bandpass', f0: 1500 + i * 200, q: 2.6, seed: cue.seed + i });
        }
        break;

      case 'zinken.reveal':
        // the crooked "?" resolving: a stamp, then the truth underneath
        this.thud(t, 200, g * 0.7, SNAP);
        this.tone({ type: 'square', f0: 520 * centsMul(cents), f1: 390, t: t + SNAP, dur: BEAT * 0.7, amp: g * 0.28, glide: 'house', filter: { type: 'lowpass', f0: 1600, q: 0.9 } });
        break;

      case 'zinken.pry':
        // a Brecheisen. Metal complaining, then a part coming free — this one is yours, so it eases.
        this.tone({ type: 'sawtooth', f0: 240 * centsMul(cents), f1: 620, t, dur: SETTLE * 0.8, amp: g * 0.35, glide: 'luck', filter: { type: 'bandpass', f0: 1400, f1: 2600, q: 4.0 } });
        this.noise({ kind: 'white', t: t + SETTLE * 0.75, dur: 0.06, amp: g * 0.5, type: 'bandpass', f0: 2600, q: 1.4, seed: cue.seed });
        this.thud(t + SETTLE * 0.8, 140, g * 0.5, SNAP);
        break;

      case 'floor.start':
        // a floor announcing itself: one low struck note per floor number, on the grid
        for (let i = 0; i < Math.min(cue.reps ?? 1, 8); i++) {
          this.tone({ type: 'triangle', f0: 165 * centsMul(cents), t: t + i * RATCHET * 3, dur: BEAT * 0.7, amp: g * 0.35, attack: 0.004, glide: 'house' });
        }
        break;

      case 'floor.end':
        // Der Croupier's castors on lino, dead constant, receding
        this.noise({ kind: 'brown', t, dur: DOLLY, amp: g * 0.35, type: 'bandpass', f0: 900, f1: 260, q: 1.0, attack: 0.05, seed: cue.seed });
        this.thud(t + DOLLY * 0.9, 60, g * 0.4, SETTLE);
        break;

      case 'quota.met': {
        // the door unlocking. A solenoid, then two notes going up — the only good news the House gives.
        this.noise({ kind: 'white', t, dur: 0.02, amp: g * 0.5, type: 'bandpass', f0: 2600, q: 2.0, seed: cue.seed });
        const f = 440 * centsMul(cents);
        this.tone({ type: 'triangle', f0: f, t: t + SNAP, dur: SETTLE * 0.6, amp: g * 0.35, glide: 'luck' });
        this.tone({ type: 'triangle', f0: f * 1.5, t: t + SNAP + RATCHET * 3, dur: SETTLE, amp: g * 0.35, glide: 'luck' });
        break;
      }

      case 'boss.door':
        // the Saalchef's door: a bolt, a long swing, air moving
        this.noise({ kind: 'white', t, dur: 0.03, amp: g * 0.5, type: 'bandpass', f0: 1800, q: 2.0, seed: cue.seed });
        this.tone({ type: 'sawtooth', f0: 90, f1: 55, t: t + SNAP, dur: SLOWMO * 0.8, amp: g * 0.3, glide: 'house', filter: { type: 'lowpass', f0: 500, q: 1.1 } });
        this.noise({ kind: 'brown', t: t + SNAP, dur: SLOWMO * 0.8, amp: g * 0.22, type: 'lowpass', f0: 700, q: 0.7, attack: 0.2, seed: cue.seed ^ 3 });
        break;

      case 'boss.telegraph': {
        // a station PA clearing its throat before it tells you a new rule. Descending
        // perfect fourth, square wave, band-limited: deadpan-institutional, not horror.
        const f = 392 * centsMul(cents);
        this.tone({ type: 'square', f0: f, t, dur: BEAT * 0.7, amp: g * 0.5, attack: 0.01, glide: 'house', filter: { type: 'bandpass', f0: 1100, q: 1.6 } });
        this.tone({ type: 'square', f0: f * 0.75, t: t + BEAT * 0.75, dur: BEAT * 0.9, amp: g * 0.5, attack: 0.01, glide: 'house', filter: { type: 'bandpass', f0: 1100, q: 1.6 } });
        break;
      }

      case 'hausverbot.stamp':
        // a rubber stamp on your record, or the same stamp lifted
        this.thud(t, cue.detail === 'on' ? 170 : 210, g * 0.9, SNAP);
        this.noise({ kind: 'white', t, dur: 0.03, amp: g * 0.4, type: 'lowpass', f0: 1800, q: 0.8, seed: cue.seed });
        this.tone({ type: 'square', f0: cue.detail === 'on' ? 330 : 220, f1: cue.detail === 'on' ? 220 : 330, t: t + SNAP, dur: BEAT * 0.6, amp: g * 0.22, glide: 'house', filter: { type: 'lowpass', f0: 1200, q: 0.9 } });
        break;

      case 'item.get': {
        const f = 660 * centsMul(cents);
        this.tone({ type: 'triangle', f0: f, f1: f * 1.26, t, dur: SETTLE, amp: g * 0.4, attack: 0.006, glide: 'luck' });
        this.noise({ kind: 'white', t, dur: 0.04, amp: g * 0.2, type: 'highpass', f0: 4200, q: 0.7, seed: cue.seed });
        break;
      }

      case 'item.lose':
        this.tone({ type: 'triangle', f0: 520 * centsMul(cents), f1: 300, t, dur: SETTLE, amp: g * 0.35, glide: 'house' });
        break;

      default:
        // An id in SOUND_IDS with no recipe yet would be a bug, but a silent one beats a
        // thrown one: the sink must never be able to take the frame loop with it.
        break;
    }
  }

  /**
   * The five bank breaks. `bankBreak` is the game's money shot and the contract says what
   * physically broke, so the breaks are genuinely different sounds — the coin rain that
   * follows is the same in all five, because the *money* is the same. The break is the
   * character; the rain is the payout.
   */
  private break_(mode: BankBreakMode | undefined, t: number, g: number, seed: number, r: () => number): void {
    switch (mode) {
      case 'axle': {
        // metal shearing: two detuned saws grinding down through a resonant band, then the
        // axle hitting the floor of the cabinet.
        this.tone({ type: 'sawtooth', f0: 148, f1: 52, t, dur: SLOWMO, amp: g * 0.4, glide: 'house', filter: { type: 'bandpass', f0: 760, f1: 300, q: 6 } });
        this.tone({ type: 'sawtooth', f0: 191, f1: 71, t, dur: SLOWMO * 0.9, amp: g * 0.3, glide: 'house', filter: { type: 'bandpass', f0: 1100, f1: 420, q: 5 } });
        this.thud(t + SLOWMO * 0.8, 44, g * 0.9, SETTLE * 1.4);
        break;
      }
      case 'wheel': {
        // the wheel off its bearing: a whine falling away, and clicks decelerating on a
        // t^1.55 curve — the sound of something losing a fight with friction.
        this.tone({ type: 'sine', f0: 900, f1: 220, t, dur: SLOWMO * 1.3, amp: g * 0.3, glide: 'luck' });
        for (let i = 0; i < 26; i++) {
          const at = t + 0.02 * Math.pow(i, 1.55);
          if (at > t + SLOWMO * 1.4) break;
          this.noise({ kind: 'white', t: at, dur: 0.006, amp: g * 0.35 * (1 - i / 26), type: 'bandpass', f0: 1800 + r() * 600, q: 2.2, seed: seed + i });
        }
        this.thud(t + SLOWMO, 70, g * 0.6, SETTLE);
        break;
      }
      case 'tray': {
        // sheet metal: three inharmonic partials with a long clang, the coin tray tearing
        // off the front of the cabinet and taking the trim with it.
        for (const [f, a] of [[208, 0.5], [317, 0.35], [489, 0.22]] as [number, number][]) {
          this.tone({ type: 'triangle', f0: f, t, dur: SLOWMO, amp: g * a, attack: 0.001 });
        }
        this.noise({ kind: 'white', t, dur: 0.05, amp: g * 0.5, type: 'bandpass', f0: 2400, q: 1.2, seed });
        this.thud(t + 0.02, 90, g * 0.7, SETTLE);
        break;
      }
      case 'chute': {
        // the payout chute bursting: a roar of coins inside a metal pipe. No single break
        // event at all — the break IS the flow, so this one is all bandpassed noise.
        this.noise({ kind: 'brown', t, dur: SETTLE * 2, amp: g * 0.55, type: 'bandpass', f0: 300, f1: 1400, q: 1.4, attack: 0.03, seed });
        this.noise({ kind: 'white', t: t + 0.02, dur: SETTLE * 1.6, amp: g * 0.3, type: 'bandpass', f0: 2600, q: 0.9, attack: 0.05, seed: seed ^ 9 });
        break;
      }
      case 'glass':
      default: {
        // glass: one whump as the pane lets go, then many small bright inharmonic grains.
        // Scattered over SETTLE, not simultaneous — simultaneous grains are a cymbal.
        this.thud(t, 130, g * 0.7, SNAP);
        for (let i = 0; i < 22; i++) {
          const at = t + SETTLE * Math.pow(r(), 1.4);
          this.noise({ kind: 'white', t: at, dur: 0.012 + r() * 0.02, amp: g * (0.10 + r() * 0.14), type: 'bandpass', f0: 2600 + r() * 6400, q: 3.4, seed: seed + i });
        }
        this.tone({ type: 'sine', f0: 5300, t, dur: SETTLE, amp: g * 0.12, attack: 0.001 });
        this.tone({ type: 'sine', f0: 7900, t, dur: SETTLE * 0.7, amp: g * 0.08, attack: 0.001 });
        break;
      }
    }
  }

  // -------------------------------------------------------------- noise buffers

  /**
   * Generate (and cache) a noise buffer. Self-made, from `hash32`, so the "random" content
   * is identical on every machine and every replay — a recorded run sounds the same twice.
   *
   * `seam` crossfades the tail into the head so a looped buffer has no audible splice. The
   * room tone runs for the whole session; a click every eight seconds would be the most
   * noticeable thing in the mix.
   */
  private buffer(ctx: WCtx, kind: 'white' | 'pink' | 'brown', seconds: number, seed: number, seam: boolean): WBuffer | null {
    const key = `${kind}:${seconds}:${seed >>> 0}:${seam ? 1 : 0}`;
    const hit = this.bufs.get(key);
    if (hit) return hit;
    try {
      const rate = ctx.sampleRate || 48000;
      const xf = seam ? Math.floor(rate * 0.25) : 0;
      const len = Math.floor(rate * seconds);
      const buf = ctx.createBuffer(1, len, rate);
      const d = buf.getChannelData(0);
      const r = rng(seed);
      if (kind === 'white') {
        for (let i = 0; i < len; i++) d[i] = r() * 2 - 1;
      } else if (kind === 'brown') {
        // leaky integrator: −6 dB/octave, the spectrum of a room and of distant machinery
        let last = 0;
        for (let i = 0; i < len; i++) {
          last = (last + 0.02 * (r() * 2 - 1)) / 1.02;
          d[i] = last * 3.5;
        }
      } else {
        // Paul Kellet's economy pink filter: −3 dB/octave with three poles
        let b0 = 0, b1 = 0, b2 = 0;
        for (let i = 0; i < len; i++) {
          const w = r() * 2 - 1;
          b0 = 0.99765 * b0 + w * 0.0990460;
          b1 = 0.96300 * b1 + w * 0.2965164;
          b2 = 0.57000 * b2 + w * 1.0526913;
          d[i] = (b0 + b1 + b2 + w * 0.1848) * 0.22;
        }
      }
      if (xf > 0 && len > xf * 2) {
        // fold the tail over the head with an equal-power ramp, then the loop is seamless
        for (let i = 0; i < xf; i++) {
          const a = Math.cos((i / xf) * Math.PI * 0.5);
          const b = Math.sin((i / xf) * Math.PI * 0.5);
          d[i] = d[i] * b + d[len - xf + i] * a;
        }
      }
      this.bufs.set(key, buf);
      return buf;
    } catch {
      return null;
    }
  }
}

/** cents → frequency multiplier. 1200 cents is an octave; this is the whole of music theory. */
function centsMul(cents: number): number {
  return Math.pow(2, cents / 1200);
}
